import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, limit, orderBy, doc, getDoc } from "firebase/firestore";
import { db, auth, storage } from "../../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { useAuthState } from 'react-firebase-hooks/auth';
import { generateDriverReport } from '../../utils/reportGenerator'; // Import the utility
import { rolesOf, isAdminOrOwner } from '../../roles';

const DriverSummary = () => {
  const [user] = useAuthState(auth);
  const [pkgList, setDeliveredPackages] = useState([]);
  const [reportHistory, setReportHistory] = useState([]); // Add this
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Was `useState(null)` with no setter, so every owner-only affordance below
  // was permanently dead. Holds the full role list, not one role.
  const [userRoles, setUserRoles] = useState([]);

  // Helper function to download files from Firebase Storage using best practices
  const downloadFromStorage = async (pdfUrl, fileName) => {
    try {
      // Parse the Firebase Storage URL to extract the file path
      let storagePath = '';
      
      if (pdfUrl.includes('firebasestorage.googleapis.com')) {
        // Handle standard Firebase Storage HTTPS URLs
        // Format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?{params}
        const urlObj = new URL(pdfUrl);
        const pathParam = urlObj.pathname.split('/o/')[1];
        if (pathParam) {
          // Decode the path and remove any query parameters
          storagePath = decodeURIComponent(pathParam.split('?')[0]);
        }
      } else if (pdfUrl.startsWith('gs://')) {
        // Handle gs:// format URLs
        // Format: gs://{bucket}/{path}
        const gsMatch = pdfUrl.match(/^gs:\/\/[^/]+\/(.+)$/);
        if (gsMatch) {
          storagePath = gsMatch[1];
        }
      }
      
      if (!storagePath) {
        throw new Error('Could not extract storage path from URL');
      }
      
      console.log('Downloading from storage path:', storagePath);
      
      // Create a reference to the file using the storage path
      const fileRef = ref(storage, storagePath);
      
      // Get download URL with proper access token (bypasses CORS)
      const downloadUrl = await getDownloadURL(fileRef);
      
      // Fetch the file as blob using the authenticated download URL
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a download link and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.style.display = 'none';
      downloadLink.href = blobUrl;
      downloadLink.download = fileName;
      
      // Append to body, click, and cleanup
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      console.log('Download initiated successfully');
      return true;
      
    } catch (error) {
      console.error('Download error:', error);
      console.error('Failed URL:', pdfUrl);
      
      // Fallback: Try to open the file in a new tab
      console.log('Attempting fallback: Opening file in new tab...');
      
      try {
        // Try to get a fresh download URL and open it
        if (pdfUrl.includes('firebasestorage.googleapis.com') || pdfUrl.startsWith('gs://')) {
          let storagePath = '';
          
          if (pdfUrl.includes('firebasestorage.googleapis.com')) {
            const urlObj = new URL(pdfUrl);
            const pathParam = urlObj.pathname.split('/o/')[1];
            if (pathParam) {
              storagePath = decodeURIComponent(pathParam.split('?')[0]);
            }
          } else if (pdfUrl.startsWith('gs://')) {
            const gsMatch = pdfUrl.match(/^gs:\/\/[^/]+\/(.+)$/);
            if (gsMatch) {
              storagePath = gsMatch[1];
            }
          }
          
          if (storagePath) {
            const fileRef = ref(storage, storagePath);
            const viewUrl = await getDownloadURL(fileRef);
            window.open(viewUrl, '_blank');
            return false;
          }
        }
        
        // If all else fails, just open the original URL
        window.open(pdfUrl, '_blank');
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        alert(`Unable to download or view file. Please try again later.\n\nError: ${error.message}`);
      }
      
      return false;
    }
  };

  useEffect(() => {
    const fetchPackages = async () => {
      if (!user) return;

      try {
        // 1. First, fetch all packages for the driver
        const querySnapshot = await getDocs(
          query(
            collection(db, "packages"), 
            where("assignedTo", "==", user.uid),
            where("status", "==", "delivered"),
            where("isPaid", "==", true),
            limit(30)
          )
        );
        
        const deliveredPackages = {};
        const packageIds = [];
        
        // 2. Build packages object and collect package IDs
        querySnapshot.forEach(doc => {
          const packageData = { id: doc.id, ...doc.data() };
          deliveredPackages[doc.id] = packageData;
          packageIds.push(doc.id); // Collect IDs for payment lookup
        });


        // 3. Fetch payment methods for all packages in batches
        if (packageIds.length > 0) {
          const paymentMap = {};
          const batchSize = 30; // Firestore 'in' query limit
          
          for (let i = 0; i < packageIds.length; i += batchSize) {
            const batchIds = packageIds.slice(i, i + batchSize);
            const paymentsQuery = query(
              collection(db, "payments"),
              where("packageId", "in", batchIds)
            );
            
            const paymentsSnapshot = await getDocs(paymentsQuery);
            paymentsSnapshot.forEach(doc => {
              const data = doc.data();
              paymentMap[data.packageId] = data.paymentMethod || "N/A";
            });
          }


          // 4. Append payment methods to each package
          Object.keys(deliveredPackages).forEach(packageId => {
            deliveredPackages[packageId].paymentMethod = paymentMap[packageId] || "No payment method recorded";
          });
        }

        // 5. Set the enriched packages
        setDeliveredPackages(deliveredPackages);
        
      } catch (error) {
        console.error("Error fetching packages:", error);
        setError("Failed to fetch packages");
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [user]);
  

  const handlePrintReport = async () => {
    // Check if user is authenticated
    if (!user) {
      alert('User not authenticated. Please log in again.');
      return;
    }


    const packagesArray = Object.values(pkgList);
    
    if (packagesArray.length === 0) {
      alert('No packages to report');
      return;
    }

    setGenerating(true);
    
    try {
      // Update the invoice counter before generating the report
      
      const { reportId, pdfBlob, fileName, reportData } = await generateDriverReport(
        packagesArray, 
        user.uid,
        { 
          shouldUpdatePackageStatus: false,
        }
      );
      
      // Auto-download the PDF to the device
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`Report generated and saved successfully!\nInvoice Number: ${reportData.invoiceNumber}\nFile downloaded to your device.`);
      
    } catch (error) {
      console.error("Error generating report:", error);
      alert(`Failed to generate report: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    const fetchReportHistory = async () => {
      if (!user) return;
      
      try {
        // User docs are keyed BY uid and carry `userId`, not `uid` — the old
        // where("uid","==",...) lookup matched nothing, so owners silently fell
        // through to the driver-scoped query and saw only their own reports.
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const roles = rolesOf(userDoc.exists() ? userDoc.data() : null);
        const canSeeAllReports = isAdminOrOwner(roles);
        setUserRoles(roles);

        let reportsQuery;
        if (canSeeAllReports) {
          // Owners can see all reports, ordered by date
          reportsQuery = query(
            collection(db, 'driverReports'),
            orderBy('reportDate', 'desc'),
            limit(20)
          );
        } else {
          // Regular users only see their own reports
          reportsQuery = query(
            collection(db, 'driverReports'),
            where('driverId', '==', user.uid),
            orderBy('reportDate', 'desc'),
            limit(10)
          );
        }
        
        const snapshot = await getDocs(reportsQuery);
        const reports = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setReportHistory(reports);
        
      } catch (error) {
        console.error('Error fetching report history:', error);
      }
    };
    
    fetchReportHistory();
  }, [user]);

  return (
    <div className="w-full min-h-screen bg-gray-50 md:bg-transparent">
      <div className="max-w-7xl mx-auto px-3 md:px-8 py-3 md:py-6">
        {/* Mobile Header - Compact */}
        <div className="md:hidden bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-white">Driver Summary</h2>
              <p className="text-xs text-blue-100 mt-0.5">{user?.email || "Loading..."}</p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          
          <button
            className="w-full flex items-center justify-center px-4 py-3 bg-white text-blue-700 rounded-xl hover:bg-blue-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold shadow-md"
            onClick={handlePrintReport}
            disabled={generating || Object.values(pkgList).length === 0}
          >
            <span className="mr-2 text-base">🖨️</span> 
            {generating ? 'Generating Report...' : 'Generate Report'}
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Driver Summary</h2>
          <button
            className="flex items-center justify-center px-4 md:px-5 py-2.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base font-medium shadow-sm"
            onClick={handlePrintReport}
            disabled={generating || Object.values(pkgList).length === 0}
          >
            <span className="mr-2">🖨️</span> 
            {generating ? 'Generating...' : 'Print Report'}
          </button>
        </div>

        {/* Mobile Quick Stats */}
        <div className="md:hidden grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl mb-1">📦</div>
            <div className="text-2xl font-bold text-gray-800">{Object.values(pkgList).length}</div>
            <div className="text-xs text-gray-500 font-medium">Awaiting Report</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="text-2xl mb-1">📋</div>
            <div className="text-2xl font-bold text-gray-800">{reportHistory.length}</div>
            <div className="text-xs text-gray-500 font-medium">Past Reports</div>
          </div>
        </div>

        {/* Driver Info */}
        <section className="bg-white rounded-xl md:rounded-lg shadow-sm md:shadow-md p-4 md:p-6 mb-4 border border-gray-100 md:border-0">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 md:hidden">Driver Information</h3>
          <div className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-6">
            <div className="flex-1">
              <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-4">
                <div>
                  <div className="text-xs md:text-sm text-gray-500 mb-1 font-medium">Name</div>
                  <div className="font-bold text-sm md:text-lg text-gray-800">{user?.name || user?.displayName || "Unknown"}</div>
                </div>
                <div className="md:hidden">
                  <div className="text-xs text-gray-500 mb-1 font-medium">Phone</div>
                  <div className="font-bold text-sm text-gray-800">{user?.phoneNumber || user?.phone || "N/A"}</div>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <div className="text-xs md:text-sm text-gray-500 mb-1 font-medium">Email</div>
                  <div className="font-semibold text-xs md:text-base text-gray-800 break-all">{user?.email || "Unknown Email"}</div>
                </div>
              </div>
            </div>
            <div className="hidden md:block md:text-right">
              <div className="text-sm text-gray-500 mt-4 mb-1">Phone</div>
              <div className="font-bold text-base text-gray-800">{user?.phoneNumber || user?.phone || "N/A"}</div>
            </div>
          </div>
        </section>

      {/* Packages Awaiting Report */}
      <section className="bg-white rounded-xl md:rounded-lg shadow-sm md:shadow-md p-4 md:p-6 mb-4 border border-gray-100 md:border-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base md:text-lg text-gray-800">
            <span className="hidden md:inline">Packages Awaiting Report</span>
            <span className="md:hidden">Awaiting Report</span>
          </h3>
          <span className="text-xs md:text-sm bg-blue-100 text-blue-800 px-2.5 py-1 rounded-full font-bold">
            {Object.values(pkgList).length}
          </span>
        </div>
        {loading ? (
          <div className="py-12 md:py-8 text-center text-gray-400">
            <div className="inline-block animate-spin rounded-full h-10 w-10 md:h-8 md:w-8 border-b-2 border-blue-600 mb-3 md:mb-2"></div>
            <div className="text-sm font-medium">Loading packages...</div>
          </div>
        ) : error ? (
          <div className="py-12 md:py-8 text-center">
            <div className="text-red-500 text-5xl md:text-4xl mb-3 md:mb-2">⚠️</div>
            <div className="text-red-600 font-bold text-sm">Error: {error}</div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="py-3 px-2 text-gray-600 font-semibold text-sm">Recipient</th>
                    <th className="py-3 px-2 text-gray-600 font-semibold text-sm">Description</th>
                    <th className="py-3 px-2 text-gray-600 font-semibold text-sm">Destination</th>
                    <th className="py-3 px-2 text-gray-600 font-semibold text-sm">Payment Method</th>
                    <th className="py-3 px-2 text-gray-600 font-semibold text-sm">Tracking #</th>
                    <th className="py-3 px-2 text-gray-600 font-semibold text-sm">Delivered On</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(pkgList).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-400">
                        No packages awaiting report.
                      </td>
                    </tr>
                  ) : (
                    Object.values(pkgList).map(pkg => (
                      <tr key={pkg.trackingNumber || pkg.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-2 text-sm">{pkg.recipient}</td>
                        <td className="py-3 px-2 text-sm">{pkg.details}</td>
                        <td className="py-3 px-2 text-sm">{pkg.destination}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            pkg.paymentMethod === 'No payment method recorded' 
                              ? 'bg-gray-100 text-gray-600' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {formatPaymentMethod(pkg.paymentMethod)}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-sm font-mono">{pkg.trackingNumber}</td>
                        <td className="py-3 px-2 text-green-600 font-semibold text-sm">
                          {pkg.deliveredOn || 'N/A'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View - Modern Design */}
            <div className="md:hidden space-y-3">
              {Object.values(pkgList).length === 0 ? (
                <div className="py-16 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div className="text-sm font-bold text-gray-400 mb-1">No packages awaiting report</div>
                  <div className="text-xs text-gray-400">Packages will appear here after delivery</div>
                </div>
              ) : (
                Object.values(pkgList).map(pkg => (
                  <div key={pkg.trackingNumber || pkg.id} className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* Card Header with Gradient */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-white text-sm mb-0.5">{pkg.recipient}</div>
                          <div className="text-xs text-blue-100 font-mono">{pkg.trackingNumber}</div>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold shadow-sm ${
                          pkg.paymentMethod === 'No payment method recorded' 
                            ? 'bg-gray-100 text-gray-700' 
                            : 'bg-green-400 text-green-900'
                        }`}>
                          {formatPaymentMethod(pkg.paymentMethod)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Card Body */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                          <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-0.5">Description</div>
                          <div className="text-sm text-gray-800 font-semibold">{pkg.details || 'N/A'}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                          <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-0.5">Destination</div>
                          <div className="text-sm text-gray-800 font-semibold">{pkg.destination}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center pt-3 border-t border-gray-200">
                        <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-0.5">Delivered On</div>
                          <div className="text-sm text-green-600 font-bold">{pkg.deliveredOn || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </section>

      {/* Report History */}
      <section className="bg-white rounded-xl md:rounded-lg shadow-sm md:shadow-md p-4 md:p-6 border border-gray-100 md:border-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base md:text-lg text-gray-800">
            <span className="block md:inline">Report History</span>
            {isAdminOrOwner(userRoles) && <span className="text-xs md:text-sm text-blue-600 block md:inline md:ml-2">(All Drivers)</span>}
          </h3>
          <span className="text-xs md:text-sm bg-purple-100 text-purple-800 px-2.5 py-1 rounded-full font-bold">
            {reportHistory.length}
          </span>
        </div>
        {reportHistory.length === 0 ? (
          <div className="py-16 md:py-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center md:bg-transparent md:w-auto md:h-auto">
              <div className="text-4xl md:text-5xl">📋</div>
            </div>
            <div className="text-gray-600 text-sm font-bold mb-1">No reports yet</div>
            <div className="text-gray-400 text-xs">Reports will appear here after generation</div>
          </div>
        ) : (
          <div className="space-y-3">
            {reportHistory.map(report => (
              <div key={report.id} className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 md:bg-gradient-to-r md:from-blue-50 md:to-indigo-50 rounded-xl md:rounded-lg border border-purple-200 md:border-blue-100 shadow-sm overflow-hidden">
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between p-4">
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">
                      {isAdminOrOwner(userRoles) && report.driverName && (
                        <span className="text-blue-600 mr-2">[{report.driverName}]</span>
                      )}
                      Report - {report.reportDate?.toDate?.()?.toLocaleDateString() || 'Unknown Date'}
                    </div>
                    <div className="text-gray-600 text-sm mt-1">
                      {report.packageCount} packages • {report.deliveredCount} delivered
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button 
                      onClick={() => window.open(report.pdfUrl, '_blank')}
                      className="text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-md border border-blue-300 hover:bg-blue-100 transition-colors text-sm font-medium"
                      title="View PDF"
                    >
                      📄 View
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          const success = await downloadFromStorage(report.pdfUrl, report.fileName || `report-${report.id}.pdf`);
                          if (success) {
                            console.log('Download completed successfully');
                          }
                        } catch (error) {
                          console.error('Download button error:', error);
                        }
                      }}
                      className="text-green-600 hover:text-green-800 px-3 py-1.5 rounded-md border border-green-300 hover:bg-green-100 transition-colors text-sm font-medium"
                      title="Download PDF"
                    >
                      💾 Download
                    </button>
                  </div>
                </div>

                {/* Mobile Layout - Modern Card Design */}
                <div className="md:hidden">
                  {/* Card Header with Gradient */}
                  <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-3">
                    {isAdminOrOwner(userRoles) && report.driverName && (
                      <div className="text-purple-100 font-semibold text-xs mb-1 flex items-center">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        {report.driverName}
                      </div>
                    )}
                    <div className="flex items-center text-white">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="font-bold text-sm">
                        {report.reportDate?.toDate?.()?.toLocaleDateString() || 'Unknown Date'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 p-4 bg-white bg-opacity-50">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-800">{report.packageCount}</div>
                      <div className="text-xs text-gray-600 font-medium">Packages</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{report.deliveredCount}</div>
                      <div className="text-xs text-gray-600 font-medium">Delivered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600">${report.totalDeliveredPrice?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-gray-600 font-medium">Total</div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3 p-4 pt-0">
                    <button 
                      onClick={() => window.open(report.pdfUrl, '_blank')}
                      className="flex items-center justify-center gap-2 bg-white text-blue-600 px-4 py-3 rounded-xl border-2 border-blue-300 active:scale-95 transition-all text-sm font-bold shadow-sm"
                      title="View PDF"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>View</span>
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          const success = await downloadFromStorage(report.pdfUrl, report.fileName || `report-${report.id}.pdf`);
                          if (success) {
                            console.log('Download completed successfully');
                          }
                        } catch (error) {
                          console.error('Download button error:', error);
                        }
                      }}
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-xl active:scale-95 transition-all text-sm font-bold shadow-md"
                      title="Download PDF"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      </div>
    </div>
  );
};

const formatPaymentMethod = (method) => {
  switch (method) {
    case 'credit_card':
      return 'Credit Card';
    case 'bank_transfer':
      return 'Bank Transfer';
    case 'cash':
      return 'Cash';
    case 'linx':
      return 'Linx';
    case 'check':
      return 'Check';
    case 'No payment method recorded':
      return 'No Payment';
    default:
      return method ? method.charAt(0).toUpperCase() + method.slice(1) : 'N/A';
  }
};

export default DriverSummary;